import { z } from 'zod';
import { IdSchema, LocalDateSchema, PageQuerySchema, pageDe } from '../common/index.js';
import * as E from '../entities/index.js';
import { LegalParameterSchema, LegalParameterCreateSchema } from '../legal/index.js';
import { route, type RouteContrat } from './route.js';

/** Routes du contrat v1, groupées par tag (architecture §3.3). */

const ids = <K extends string>(...noms: K[]) => z.object(Object.fromEntries(noms.map((n) => [n, IdSchema])) as Record<K, typeof IdSchema>);
const D = ids('dossierId');
const V = '/api/v1';

const auth: RouteContrat[] = [
  route({
    id: 'auth.connexion',
    methode: 'post',
    chemin: `${V}/auth/connexion`,
    tag: 'auth',
    resume: 'Connexion de l’équipe (mot de passe et TOTP).',
    corps: E.LoginSchema,
    reponse: E.SessionSchema,
    permission: 'public',
    actionAudit: 'auth.connexion',
  }),
  route({
    id: 'auth.lienMagique',
    methode: 'post',
    chemin: `${V}/auth/lien-magique`,
    tag: 'auth',
    resume: 'Ouverture du portail par lien magique ou code court.',
    corps: E.MagicLinkSchema,
    reponse: E.SessionSchema,
    permission: 'public',
    actionAudit: 'auth.lien_magique',
    erreurs: ['LINK_EXPIRED'],
  }),
  route({
    id: 'auth.moi',
    methode: 'get',
    chemin: `${V}/auth/moi`,
    tag: 'auth',
    resume: 'Session courante, rôles et bandeau « voir en tant que ».',
    reponse: E.SessionSchema,
    permission: 'session:read:self',
  }),
  route({
    id: 'auth.otp.demander',
    methode: 'post',
    chemin: `${V}/auth/otp`,
    tag: 'auth',
    resume: 'Envoi d’un code de double contrôle pour une action sensible.',
    corps: E.OtpRequestSchema,
    reponse: E.OtpChallengeSchema,
    permission: 'session:write:self',
    actionAudit: 'auth.otp.demande',
    erreurs: ['RATE_LIMITED'],
  }),
  route({
    id: 'auth.otp.verifier',
    methode: 'post',
    chemin: `${V}/auth/otp/verification`,
    tag: 'auth',
    resume: 'Vérification du code de double contrôle.',
    corps: E.OtpVerifySchema,
    reponse: E.SessionSchema,
    permission: 'session:write:self',
    actionAudit: 'auth.otp.verifie',
    erreurs: ['OTP_REQUIRED'],
  }),
  route({
    id: 'auth.deconnexion',
    methode: 'post',
    chemin: `${V}/auth/deconnexion`,
    tag: 'auth',
    resume: 'Fermeture de la session courante.',
    statut: 204,
    descriptionReponse: 'Session fermée.',
    permission: 'session:write:self',
    actionAudit: 'auth.deconnexion',
  }),
];

const impersonation: RouteContrat[] = [
  route({
    id: 'impersonation.demarrer',
    methode: 'post',
    chemin: `${V}/impersonation`,
    tag: 'impersonation',
    resume: 'Commencer à voir Lakou en tant qu’un autre utilisateur.',
    corps: E.ImpersonationStartSchema,
    reponse: E.ImpersonationSessionSchema,
    permission: 'impersonation:act:all',
    actionAudit: 'impersonation.debut',
  }),
  route({
    id: 'impersonation.confirmer',
    methode: 'post',
    chemin: `${V}/impersonation/{sessionId}/confirmations`,
    tag: 'impersonation',
    resume: 'Confirmer explicitement une action en tant que.',
    parametres: ids('sessionId'),
    corps: E.ImpersonationConfirmSchema,
    reponse: E.ImpersonationConfirmationSchema,
    permission: 'impersonation:act:all',
    actionAudit: 'impersonation.confirmation',
    erreurs: ['IMPERSONATION_READ_ONLY'],
  }),
  route({
    id: 'impersonation.terminer',
    methode: 'post',
    chemin: `${V}/impersonation/{sessionId}/fin`,
    tag: 'impersonation',
    resume: 'Revenir à sa propre vue.',
    parametres: ids('sessionId'),
    reponse: E.ImpersonationSessionSchema,
    permission: 'impersonation:act:all',
    actionAudit: 'impersonation.fin',
  }),
  route({
    id: 'impersonation.historique',
    methode: 'get',
    chemin: `${V}/impersonation`,
    tag: 'impersonation',
    resume: 'Historique des sessions « voir en tant que ».',
    requete: PageQuerySchema,
    reponse: pageDe(E.ImpersonationSessionSchema),
    permission: 'impersonation:read:all',
  }),
];

const dossiers: RouteContrat[] = [
  route({
    id: 'dossiers.pilotage',
    methode: 'get',
    chemin: `${V}/pilotage`,
    tag: 'dossiers',
    resume: 'Tableau de bord : dossiers triés par blocage et valeur, encaissements.',
    reponse: E.DashboardSchema,
    permission: 'pilotage:read:all',
  }),
  route({
    id: 'dossiers.lister',
    methode: 'get',
    chemin: `${V}/dossiers`,
    tag: 'dossiers',
    resume: 'Liste des dossiers, filtrée par phase, blocage ou étude.',
    requete: PageQuerySchema.extend({
      phase: E.PhaseSchema.optional(),
      bloque: z.enum(['oui', 'non']).optional(),
      etudeId: IdSchema.optional(),
    }),
    reponse: pageDe(E.DossierSummarySchema),
    permission: 'dossier:read:dossier',
  }),
  route({
    id: 'dossiers.creer',
    methode: 'post',
    chemin: `${V}/dossiers`,
    tag: 'dossiers',
    resume: 'Créer un dossier.',
    corps: E.DossierCreateSchema,
    reponse: E.DossierStaffViewSchema,
    permission: 'dossier:write:dossier',
    actionAudit: 'dossier.creation',
  }),
  route({
    id: 'dossiers.lire',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}`,
    tag: 'dossiers',
    resume: 'Vue d’ensemble d’un dossier.',
    parametres: D,
    reponse: E.DossierStaffViewSchema,
    permission: 'dossier:read:dossier',
  }),
  route({
    id: 'dossiers.modifier',
    methode: 'patch',
    chemin: `${V}/dossiers/{dossierId}`,
    tag: 'dossiers',
    resume: 'Modifier un dossier (titre, statut, blocage).',
    parametres: D,
    corps: E.DossierPatchSchema,
    reponse: E.DossierStaffViewSchema,
    permission: 'dossier:write:dossier',
    actionAudit: 'dossier.modification',
  }),
  route({
    id: 'dossiers.journal',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/journal`,
    tag: 'dossiers',
    resume: 'Journal horodaté du dossier.',
    parametres: D,
    requete: PageQuerySchema,
    reponse: pageDe(E.TimelineEntrySchema),
    permission: 'dossier:read:dossier',
  }),
  route({
    id: 'dossiers.journal.exporter',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/journal/export`,
    tag: 'dossiers',
    resume: 'Exporter le journal en PDF (sans coulisses pour un destinataire externe).',
    parametres: D,
    requete: z.object({ destinataire: z.enum(['equipe', 'notaire', 'juge']) }),
    reponse: E.ExportSchema,
    permission: 'journal:export:dossier',
    actionAudit: 'journal.export',
  }),
  // PARITE-4 : comptes rendus d'entretien et d'appel (réunion du 16/10 #31, RG-26), internes au cabinet.
  route({
    id: 'comptesRendus.lister',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/comptes-rendus`,
    tag: 'dossiers',
    resume: 'Comptes rendus d’entretien et d’appel du dossier (internes au cabinet).',
    parametres: D,
    reponse: z.array(E.CompteRenduSchema),
    permission: 'compte_rendu:read:dossier',
  }),
  route({
    id: 'comptesRendus.creer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/comptes-rendus`,
    tag: 'dossiers',
    resume: 'Ajouter un compte rendu à faire.',
    parametres: D,
    corps: E.CompteRenduCreateSchema,
    reponse: E.CompteRenduSchema,
    permission: 'compte_rendu:write:dossier',
    actionAudit: 'compte_rendu.creation',
  }),
  // RG-24 (RFC-RG24-001) : temps passé dans le dossier, mesuré par le chronomètre de D03, corrigeable avec un motif. Équipe seule.
  route({
    id: 'temps.dossier',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/temps`,
    tag: 'dossiers',
    resume: 'Temps passé dans le dossier : total, périodes et corrections.',
    parametres: D,
    reponse: E.TempsDossierSchema,
    permission: 'time:read:dossier',
  }),
  route({
    id: 'temps.enregistrer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/temps`,
    tag: 'dossiers',
    resume: 'Enregistrer une période passée dans le dossier (chronomètre).',
    parametres: D,
    corps: E.TempsPeriodeCreateSchema,
    reponse: E.TempsDossierSchema,
    permission: 'time:write:dossier',
    actionAudit: 'temps.enregistrement',
  }),
  route({
    id: 'temps.corriger',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/temps/corrections`,
    tag: 'dossiers',
    resume: 'Corriger le temps passé : nouveau total et motif ; l’écart est gardé.',
    parametres: D,
    corps: E.TempsCorrectionCreateSchema,
    reponse: E.TempsDossierSchema,
    permission: 'time:write:dossier',
    actionAudit: 'temps.correction',
  }),
  // L64 (30/10, vocaux d'Isabelle du 28/10) : avancement du démarrage guidé du dossier (écran D17). Équipe de bureau seule.
  route({
    id: 'demarrage.lire',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/demarrage`,
    tag: 'dossiers',
    resume: 'Avancement du démarrage guidé : étapes faites, sans objet, réponses aux questions.',
    parametres: D,
    reponse: E.DemarrageAvancementSchema,
    permission: 'demarrage:read:dossier',
  }),
  route({
    id: 'demarrage.marquer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/demarrage/{codeEtape}`,
    tag: 'dossiers',
    resume: 'Marquer une étape du démarrage guidé faite, sans objet (avec motif), ou la rouvrir.',
    parametres: z.object({ dossierId: IdSchema, codeEtape: E.CodeEtapeDemarrageSchema }),
    corps: E.DemarrageMarquageSchema,
    reponse: E.DemarrageAvancementSchema,
    permission: 'demarrage:write:dossier',
    actionAudit: 'demarrage.etape',
  }),
  route({
    id: 'demarrage.repondre',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/demarrage/{codeEtape}/reponse`,
    tag: 'dossiers',
    resume: 'Enregistrer la réponse à la question d’une étape du démarrage guidé (modifiable).',
    parametres: z.object({ dossierId: IdSchema, codeEtape: E.CodeEtapeDemarrageSchema }),
    corps: E.DemarrageReponseSchema,
    reponse: E.DemarrageAvancementSchema,
    permission: 'demarrage:write:dossier',
    actionAudit: 'demarrage.reponse',
  }),
  // Couloir G, point 4 : une condition de passage non évaluable se coche à la main.
  route({
    id: 'demarrage.cocher',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/demarrage/{codeEtape}/conditions`,
    tag: 'dossiers',
    resume: 'Cocher ou décocher une condition de passage d’une étape du démarrage guidé.',
    parametres: z.object({ dossierId: IdSchema, codeEtape: E.CodeEtapeDemarrageSchema }),
    corps: E.DemarrageCocheSchema,
    reponse: E.DemarrageAvancementSchema,
    permission: 'demarrage:write:dossier',
    actionAudit: 'demarrage.condition',
  }),
  // Couloir G, point 5 (décision de Joel du 30/10) : jamais bloqué. Forcer est réservé à la dirigeante (vérifié par le handler) ;
  // l'assistante demande le déblocage. Chemin hors de `/demarrage/{codeEtape}` pour ne jamais être lu comme un code d'étape.
  route({
    id: 'demarrage.forcer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/forcages`,
    tag: 'dossiers',
    resume: 'Forcer une étape du démarrage guidé ou le blocage du mandat de l’arbre, avec un motif (dirigeante seulement, tracé).',
    parametres: D,
    corps: E.ForcageCreateSchema,
    reponse: E.DemarrageAvancementSchema,
    permission: 'demarrage:write:dossier',
    actionAudit: 'demarrage.forcage',
    erreurs: ['FORBIDDEN'],
  }),
  route({
    id: 'demarrage.demanderDeblocage',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/forcages/demandes`,
    tag: 'dossiers',
    resume: 'Prévenir la dirigeante qu’une étape attend son déblocage : une tâche lui est créée.',
    parametres: D,
    corps: E.DemandeDeblocageCreateSchema,
    reponse: E.DemandeDeblocageSchema,
    permission: 'demarrage:write:dossier',
    actionAudit: 'demarrage.demande_deblocage',
  }),
  // Couloir G, point 6 : dépôt d'un vocal à l'étape 1 (entretien d'accueil), par morceaux, puis transcription si un service est configuré.
  route({
    id: 'accueil.vocaux.lister',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/vocaux`,
    tag: 'dossiers',
    resume: 'Vocaux déposés au dossier et leur transcription (à vérifier).',
    parametres: D,
    reponse: z.array(E.VocalSchema),
    permission: 'demarrage:read:dossier',
  }),
  route({
    id: 'accueil.vocaux.ouvrir',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/vocaux`,
    tag: 'dossiers',
    resume: 'Ouvrir le dépôt d’un vocal au dossier.',
    parametres: D,
    corps: E.VocalCreateSchema,
    reponse: E.VocalSchema,
    permission: 'demarrage:write:dossier',
    actionAudit: 'accueil.vocal.depot',
  }),
  route({
    id: 'accueil.vocaux.morceau',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/vocaux/{vocalId}/morceaux`,
    tag: 'dossiers',
    resume: 'Ajouter le morceau suivant du vocal (base64).',
    parametres: z.object({ dossierId: IdSchema, vocalId: IdSchema }),
    corps: E.VocalMorceauSchema,
    reponse: E.VocalSchema,
    permission: 'demarrage:write:dossier',
    actionAudit: 'accueil.vocal.morceau',
  }),
  route({
    id: 'accueil.vocaux.terminer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/vocaux/{vocalId}/fin`,
    tag: 'dossiers',
    resume: 'Terminer le dépôt : le vocal est transcrit si un service est configuré, sinon il reste « transcription en attente ».',
    parametres: z.object({ dossierId: IdSchema, vocalId: IdSchema }),
    corps: z.object({}).meta({ id: 'VocalFin' }),
    reponse: E.VocalSchema,
    permission: 'demarrage:write:dossier',
    actionAudit: 'accueil.vocal.fin',
  }),
  // Couloir G, point 1 : mode guidé ou avancé, gardé par utilisateur ; tableau du poste de pilotage guidé (D18).
  route({
    id: 'demarrage.mode.lire',
    methode: 'get',
    chemin: `${V}/moi/mode-travail`,
    tag: 'dossiers',
    resume: 'Mode de travail choisi par l’utilisateur (guidé ou avancé), null s’il n’a pas encore choisi.',
    reponse: E.ModeTravailLuSchema,
    permission: 'demarrage:read:dossier',
  }),
  route({
    id: 'demarrage.mode.choisir',
    methode: 'post',
    chemin: `${V}/moi/mode-travail`,
    tag: 'dossiers',
    resume: 'Choisir son mode de travail (guidé ou avancé) ; modifiable à tout moment.',
    corps: E.ModeTravailChoixSchema,
    reponse: E.ModeTravailChoixSchema,
    permission: 'demarrage:write:dossier',
    actionAudit: 'demarrage.mode',
  }),
  route({
    id: 'demarrage.tableau',
    methode: 'get',
    chemin: `${V}/demarrage/tableau`,
    tag: 'dossiers',
    resume: 'Dossiers en cours et avancement de leur démarrage guidé (poste de pilotage du mode guidé).',
    reponse: E.TableauGuideSchema,
    permission: 'demarrage:read:dossier',
  }),
  route({
    id: 'comptesRendus.generer',
    methode: 'post',
    chemin: `${V}/comptes-rendus/{compteRenduId}/generation`,
    tag: 'dossiers',
    resume: 'Générer le compte rendu depuis le texte de l’entretien (modèle versionné, toujours « à vérifier »).',
    parametres: ids('compteRenduId'),
    corps: E.CompteRenduGenerationSchema,
    statut: 200,
    reponse: E.CompteRenduSchema,
    permission: 'compte_rendu:write:dossier',
    actionAudit: 'compte_rendu.generation',
  }),
  route({
    id: 'comptesRendus.modifier',
    methode: 'patch',
    chemin: `${V}/comptes-rendus/{compteRenduId}`,
    tag: 'dossiers',
    resume: 'Corriger un compte rendu généré, avant sa validation.',
    parametres: ids('compteRenduId'),
    corps: E.CompteRenduPatchSchema,
    reponse: E.CompteRenduSchema,
    permission: 'compte_rendu:write:dossier',
    actionAudit: 'compte_rendu.modification',
  }),
  route({
    id: 'comptesRendus.valider',
    methode: 'post',
    chemin: `${V}/comptes-rendus/{compteRenduId}/validation`,
    tag: 'dossiers',
    resume: 'Valider un compte rendu relu (version lue exigée) ; il ne se modifie plus.',
    parametres: ids('compteRenduId'),
    corps: E.CompteRenduValidationSchema,
    statut: 200,
    reponse: E.CompteRenduSchema,
    permission: 'compte_rendu:validate:dossier',
    actionAudit: 'compte_rendu.validation',
    erreurs: ['VERSION_CONFLICT'],
  }),
  route({
    id: 'comptesRendus.aTraiter',
    methode: 'get',
    chemin: `${V}/comptes-rendus`,
    tag: 'dossiers',
    resume: 'Comptes rendus à faire, à relire et derniers validés, tous dossiers (carte du Pilotage).',
    reponse: E.ComptesRendusATraiterSchema,
    permission: 'compte_rendu:read:all',
  }),
];

const procedure: RouteContrat[] = [
  route({
    id: 'procedure.etapes',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/etapes`,
    tag: 'procedure',
    resume: 'Étapes de procédure du dossier.',
    parametres: D,
    reponse: z.array(E.StepSchema),
    permission: 'dossier:read:dossier',
  }),
  route({
    id: 'procedure.ajouter',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/etapes`,
    tag: 'procedure',
    resume: 'Ajouter une étape (tutelle, prescription, sommation…).',
    parametres: D,
    corps: E.StepCreateSchema,
    reponse: E.StepSchema,
    permission: 'procedure:write:dossier',
    actionAudit: 'etape.ajout',
  }),
  route({
    id: 'procedure.reordonner',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/etapes/ordre`,
    tag: 'procedure',
    resume: 'Réordonner les étapes.',
    parametres: D,
    corps: E.StepOrderSchema,
    reponse: z.array(E.StepSchema),
    permission: 'procedure:write:dossier',
    actionAudit: 'etape.ordre',
  }),
  route({
    id: 'procedure.sauter',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/etapes/{etapeId}/saut`,
    tag: 'procedure',
    resume: 'Sauter une étape, avec motif.',
    parametres: ids('dossierId', 'etapeId'),
    corps: E.StepSkipSchema,
    reponse: E.StepSchema,
    permission: 'procedure:write:dossier',
    actionAudit: 'etape.saut',
  }),
];

const tasks: RouteContrat[] = [
  route({
    id: 'taches.fileDuJour',
    methode: 'get',
    chemin: `${V}/taches`,
    tag: 'tasks',
    resume: 'File du jour : appels, relances à valider, pièces, retours notaire.',
    requete: PageQuerySchema.extend({ jour: LocalDateSchema.optional() }),
    reponse: pageDe(E.TaskSchema),
    permission: 'task:read:self',
  }),
  route({
    id: 'taches.creer',
    methode: 'post',
    chemin: `${V}/taches`,
    tag: 'tasks',
    resume: 'Créer une tâche.',
    corps: E.TaskCreateSchema,
    reponse: E.TaskSchema,
    permission: 'task:write:dossier',
    actionAudit: 'tache.creation',
  }),
  route({
    id: 'taches.modifier',
    methode: 'patch',
    chemin: `${V}/taches/{tacheId}`,
    tag: 'tasks',
    resume: 'Marquer faite ou reporter une tâche.',
    parametres: ids('tacheId'),
    corps: E.TaskPatchSchema,
    reponse: E.TaskSchema,
    permission: 'task:write:dossier',
    actionAudit: 'tache.modification',
  }),
];

const decisions: RouteContrat[] = [
  route({
    id: 'decisions.lister',
    methode: 'get',
    chemin: `${V}/decisions`,
    tag: 'decisions',
    resume: 'Décisions en attente d’Isabelle.',
    reponse: z.array(E.DecisionSchema),
    permission: 'decision:read:all',
  }),
  route({
    id: 'decisions.trancher',
    methode: 'post',
    chemin: `${V}/decisions/{decisionId}/reponse`,
    tag: 'decisions',
    resume: 'Trancher une décision.',
    parametres: ids('decisionId'),
    corps: E.DecisionAnswerSchema,
    reponse: E.DecisionSchema,
    permission: 'decision:write:all',
    actionAudit: 'decision.reponse',
  }),
];

const genealogy: RouteContrat[] = [
  route({
    id: 'genealogie.arbre',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/arbre`,
    tag: 'genealogy',
    resume: 'Arbre complet, successions et parts calculées.',
    parametres: D,
    reponse: E.FamilyTreeSchema,
    permission: 'genealogy:read:dossier',
  }),
  route({
    id: 'genealogie.personnes.creer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/personnes`,
    tag: 'genealogy',
    resume: 'Ajouter une personne à l’arbre (recherche sous mandat signé).',
    parametres: D,
    corps: E.PersonWriteSchema,
    reponse: E.PersonStaffViewSchema,
    permission: 'genealogy:write:dossier',
    actionAudit: 'personne.ajout',
    erreurs: ['MANDATE_REQUIRED'],
  }),
  route({
    id: 'genealogie.personnes.rechercher',
    methode: 'get',
    chemin: `${V}/personnes`,
    tag: 'genealogy',
    resume: 'Recherche floue de personnes.',
    requete: PageQuerySchema.extend({ q: z.string().min(2), dossierId: IdSchema.optional() }),
    reponse: pageDe(E.PersonSummarySchema),
    permission: 'genealogy:read:dossier',
  }),
  route({
    id: 'genealogie.personne.lire',
    methode: 'get',
    chemin: `${V}/personnes/{personId}`,
    tag: 'genealogy',
    resume: 'Fiche héritier.',
    parametres: ids('personId'),
    reponse: E.PersonStaffViewSchema,
    permission: 'person.contact:read:dossier',
  }),
  route({
    id: 'genealogie.personne.modifier',
    methode: 'patch',
    chemin: `${V}/personnes/{personId}`,
    tag: 'genealogy',
    resume: 'Modifier l’état civil d’une personne.',
    parametres: ids('personId'),
    corps: E.PersonPatchSchema,
    reponse: E.PersonStaffViewSchema,
    permission: 'genealogy:write:dossier',
    actionAudit: 'personne.modification',
  }),
  route({
    id: 'genealogie.coordonnees.ajouter',
    methode: 'post',
    chemin: `${V}/personnes/{personId}/coordonnees`,
    tag: 'genealogy',
    resume: 'Ajouter une coordonnée à une personne (chiffrée, visibilité équipe par défaut).',
    parametres: ids('personId'),
    corps: E.ContactCreateSchema,
    reponse: E.ContactStaffViewSchema,
    permission: 'genealogy:write:dossier',
    actionAudit: 'personne.coordonnee.ajout',
  }),
  route({
    id: 'genealogie.relations.creer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/relations`,
    tag: 'genealogy',
    resume: 'Ajouter un lien de filiation ou d’union.',
    parametres: D,
    corps: E.RelationshipCreateSchema,
    reponse: E.RelationshipSchema,
    permission: 'genealogy:write:dossier',
    actionAudit: 'relation.ajout',
    erreurs: ['MANDATE_REQUIRED'],
  }),
  route({
    id: 'genealogie.fratrie.ajouter',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/fratrie`,
    tag: 'genealogy',
    resume: 'Ajouter un frère ou une sœur (germain ou demi) : mêmes parents, parent « à rechercher » créé au besoin.',
    parametres: D,
    corps: E.SiblingCreateSchema,
    reponse: E.SiblingCreatedSchema,
    permission: 'genealogy:write:dossier',
    actionAudit: 'fratrie.ajout',
    erreurs: ['MANDATE_REQUIRED'],
  }),
  route({
    id: 'genealogie.options.enregistrer',
    methode: 'post',
    chemin: `${V}/successions/{successionId}/options`,
    tag: 'genealogy',
    resume: 'Enregistrer l’option successorale d’un héritier.',
    parametres: ids('successionId'),
    corps: E.HeirOptionSchema.omit({ successionId: true }),
    reponse: E.HeirOptionSchema,
    permission: 'genealogy:write:dossier',
    actionAudit: 'option.enregistrement',
  }),
  route({
    id: 'genealogie.devolution',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/devolution`,
    tag: 'genealogy',
    resume: 'Dévolution de chaque succession, avec trace d’explication.',
    parametres: D,
    reponse: z.array(E.DevolutionResultSchema),
    permission: 'genealogy:read:dossier',
  }),
  route({
    id: 'genealogie.tableaux.exporter',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/tableaux`,
    tag: 'genealogy',
    resume: 'Exporter le tableau des vivants ou des défunts (PDF) ou l’arbre (GEDCOM).',
    parametres: D,
    requete: z.object({ tableau: z.enum(['vivants', 'defunts', 'arbre']), format: z.enum(['pdf', 'gedcom']) }),
    reponse: E.ExportSchema,
    permission: 'genealogy:read:dossier',
    actionAudit: 'tableaux.export',
  }),
  // PARITE-5 : import de contacts (CSV) d'un dossier, en deux temps : aperçu sans écriture, puis confirmation du même fichier.
  route({
    id: 'genealogie.contacts.apercu',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/import-contacts/apercu`,
    tag: 'genealogy',
    resume: 'Aperçu d’un import de contacts (CSV) : lignes, dix premières, doublons et erreurs ; rien n’est enregistré.',
    parametres: D,
    corps: E.ImportContactsRequestSchema,
    reponse: E.ImportContactsPreviewSchema,
    permission: 'contact:import:dossier',
    actionAudit: 'contacts.import_apercu',
  }),
  route({
    id: 'genealogie.contacts.importer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/import-contacts`,
    tag: 'genealogy',
    resume: 'Importer les contacts confirmés (fichier et nombre de l’aperçu) ; coordonnées chiffrées, aucun envoi.',
    parametres: D,
    corps: E.ImportContactsConfirmSchema,
    reponse: E.ImportContactsReportSchema,
    permission: 'contact:import:dossier',
    actionAudit: 'contacts.import',
    erreurs: ['MANDATE_REQUIRED', 'VERSION_CONFLICT'],
  }),
];

const assets: RouteContrat[] = [
  route({
    id: 'biens.lister',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/biens`,
    tag: 'assets',
    resume: 'Biens du dossier et état des titres.',
    parametres: D,
    reponse: z.array(E.AssetSchema),
    permission: 'asset:read:dossier',
  }),
  route({
    id: 'biens.creer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/biens`,
    tag: 'assets',
    resume: 'Ajouter un bien.',
    parametres: D,
    corps: E.AssetCreateSchema,
    reponse: E.AssetSchema,
    permission: 'asset:write:dossier',
    actionAudit: 'bien.ajout',
  }),
];

const estimates: RouteContrat[] = [
  route({
    id: 'estimations.lire',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/estimation`,
    tag: 'estimates',
    resume: 'Estimation des parts à l’instant T (équipe seulement, mention indicative).',
    parametres: D,
    requete: z.object({ scenario: E.ScenarioSchema.default('vendre') }),
    reponse: E.EstimateSchema,
    permission: 'estimate:read:dossier',
  }),
];

const notary: RouteContrat[] = [
  route({
    id: 'notaire.interrogations',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/interrogations`,
    tag: 'notary',
    resume: 'Interrogations FCDDV, FICOBA, FICOVIE, DGFiP, CGSS, SPF.',
    parametres: D,
    reponse: z.array(E.NotaryInquirySchema),
    permission: 'notary:read:dossier',
  }),
  route({
    id: 'notaire.interrogations.modifier',
    methode: 'patch',
    chemin: `${V}/interrogations/{interrogationId}`,
    tag: 'notary',
    resume: 'Mettre à jour une interrogation (envoyée, revenue).',
    parametres: ids('interrogationId'),
    corps: E.NotaryInquiryPatchSchema,
    reponse: E.NotaryInquirySchema,
    permission: 'notary:write:dossier',
    actionAudit: 'interrogation.modification',
  }),
  route({
    id: 'notaire.renonciations',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/renonciations`,
    tag: 'notary',
    resume: 'Renonciations déposées.',
    parametres: D,
    reponse: z.array(E.RenunciationSchema),
    permission: 'notary:read:dossier',
  }),
  // PARITE-4 : actes notariés du dossier (réunion du 16/10 #33), fichier dans la GED, équipe MAAT seulement.
  route({
    id: 'actesNotaries.lister',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/actes-notaries`,
    tag: 'notary',
    resume: 'Actes notariés du dossier, types du catalogue versionné et successions du dossier.',
    parametres: D,
    reponse: E.ActesNotariesDossierSchema,
    permission: 'acte:read:dossier',
  }),
  route({
    id: 'actesNotaries.creer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/actes-notaries`,
    tag: 'notary',
    resume: 'Enregistrer un acte notarié (déjà obtenu ou à établir), rattaché à une pièce de la GED.',
    parametres: D,
    corps: E.ActeNotarieCreateSchema,
    reponse: E.ActeNotarieSchema,
    permission: 'acte:write:dossier',
    actionAudit: 'acte_notarie.creation',
  }),
  route({
    id: 'actesNotaries.modifier',
    methode: 'patch',
    chemin: `${V}/actes-notaries/{acteId}`,
    tag: 'notary',
    resume: 'Modifier un acte notarié (statut, date, étude, référence, pièce).',
    parametres: ids('acteId'),
    corps: E.ActeNotariePatchSchema,
    reponse: E.ActeNotarieSchema,
    permission: 'acte:write:dossier',
    actionAudit: 'acte_notarie.modification',
  }),
  route({
    id: 'actesNotaries.retirer',
    methode: 'delete',
    chemin: `${V}/actes-notaries/{acteId}`,
    tag: 'notary',
    resume: 'Retirer un acte enregistré par erreur (retrait logique, journalisé).',
    parametres: ids('acteId'),
    statut: 204,
    descriptionReponse: 'Acte retiré.',
    permission: 'acte:write:dossier',
    actionAudit: 'acte_notarie.retrait',
  }),
];

const deadlines: RouteContrat[] = [
  route({
    id: 'echeances.lister',
    methode: 'get',
    chemin: `${V}/echeances`,
    tag: 'deadlines',
    resume: 'Échéances légales à venir.',
    requete: z.object({ dossierId: IdSchema.optional(), avant: LocalDateSchema.optional() }),
    reponse: z.array(E.DeadlineSchema),
    permission: 'deadline:read:all',
  }),
];

const documents: RouteContrat[] = [
  route({
    id: 'documents.lister',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/documents`,
    tag: 'documents',
    resume: 'Pièces du dossier, par personne et par bien.',
    parametres: D,
    reponse: z.array(E.DocumentSchema),
    permission: 'document:read:dossier',
  }),
  route({
    id: 'documents.televerser',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/documents`,
    tag: 'documents',
    resume: 'Ouvrir un téléversement reprenable.',
    parametres: D,
    corps: E.DocumentUploadSchema,
    reponse: E.UploadSessionSchema,
    permission: 'document:write:dossier',
    actionAudit: 'document.televersement',
  }),
  route({
    id: 'documents.lire',
    methode: 'get',
    chemin: `${V}/documents/{documentId}`,
    tag: 'documents',
    resume: 'Une pièce, sa version courante et son aperçu.',
    parametres: ids('documentId'),
    reponse: E.DocumentSchema,
    permission: 'document:read:dossier',
  }),
  route({
    id: 'documents.examiner',
    methode: 'post',
    chemin: `${V}/documents/{documentId}/examen`,
    tag: 'documents',
    resume: 'Valider, refuser ou demander une meilleure photo.',
    parametres: ids('documentId'),
    corps: E.DocumentReviewSchema,
    reponse: E.DocumentSchema,
    permission: 'document:validate:dossier',
    actionAudit: 'document.examen',
  }),
  route({
    id: 'documents.attendus',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/pieces-attendues`,
    tag: 'documents',
    resume: 'Checklist des pièces attendues.',
    parametres: D,
    reponse: z.array(E.DocumentRequirementSchema),
    permission: 'document:read:dossier',
  }),
  // PARITE-5 : vidéos du dossier (réunion du 16/10 #54) : adresses YouTube (aucun fichier, rien dans la GED), équipe seulement ;
  // partage au portail de la famille par un bouton explicite et une alerte qui liste les destinataires.
  route({
    id: 'videos.lister',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/videos`,
    tag: 'documents',
    resume: 'Vidéos du dossier et destinataires possibles d’un partage au portail de la famille.',
    parametres: D,
    reponse: E.VideosDossierSchema,
    permission: 'video:read:dossier',
  }),
  route({
    id: 'videos.rattacher',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/videos`,
    tag: 'documents',
    resume: 'Rattacher une vidéo YouTube au dossier (réservée à l’équipe).',
    parametres: D,
    corps: E.VideoCreateSchema,
    reponse: E.VideoDossierSchema,
    permission: 'video:write:dossier',
    actionAudit: 'video.rattachement',
  }),
  route({
    id: 'videos.retirer',
    methode: 'delete',
    chemin: `${V}/videos/{videoId}`,
    tag: 'documents',
    resume: 'Retirer une vidéo du dossier (et du portail si elle y était partagée).',
    parametres: ids('videoId'),
    statut: 204,
    descriptionReponse: 'Vidéo retirée.',
    permission: 'video:write:dossier',
    actionAudit: 'video.retrait',
  }),
  route({
    id: 'videos.partager',
    methode: 'post',
    chemin: `${V}/videos/{videoId}/partage`,
    tag: 'documents',
    resume: 'Partager une vidéo au portail de la famille, après l’alerte qui liste les destinataires.',
    parametres: ids('videoId'),
    corps: E.VideoShareSchema,
    statut: 200,
    reponse: E.VideoDossierSchema,
    permission: 'video:share:dossier',
    actionAudit: 'video.partage',
    erreurs: ['VERSION_CONFLICT'],
  }),
  route({
    id: 'videos.partage.retirer',
    methode: 'delete',
    chemin: `${V}/videos/{videoId}/partage`,
    tag: 'documents',
    resume: 'Retirer une vidéo du portail de la famille.',
    parametres: ids('videoId'),
    reponse: E.VideoDossierSchema,
    permission: 'video:share:dossier',
    actionAudit: 'video.partage_retrait',
  }),
];

const mandates: RouteContrat[] = [
  route({
    id: 'mandats.lister',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/mandats`,
    tag: 'mandates',
    resume: 'Mandats, devis et procurations du dossier.',
    parametres: D,
    reponse: z.array(E.MandateSchema),
    permission: 'mandate:read:dossier',
  }),
  route({
    id: 'mandats.envoyer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/mandats`,
    tag: 'mandates',
    resume: 'Envoyer un document à signer (DocuSeal).',
    parametres: D,
    corps: E.MandateSendSchema,
    reponse: E.MandateSchema,
    permission: 'mandate:write:dossier',
    actionAudit: 'mandat.envoi',
  }),
];

const messaging: RouteContrat[] = [
  route({
    id: 'messagerie.fils',
    methode: 'get',
    chemin: `${V}/fils`,
    tag: 'messaging',
    resume: 'Boîte unifiée : WhatsApp d’Isabelle et de Brigitte, e-mails.',
    requete: PageQuerySchema.extend({ statut: z.enum(['rattache', 'non_rattache']).optional(), dossierId: IdSchema.optional() }),
    reponse: pageDe(E.ThreadSchema),
    permission: 'message:read:all',
  }),
  route({
    id: 'messagerie.fil',
    methode: 'get',
    chemin: `${V}/fils/{filId}`,
    tag: 'messaging',
    resume: 'Un fil et ses messages (vocaux transcrits, pièces).',
    parametres: ids('filId'),
    reponse: E.ThreadDetailSchema,
    permission: 'message:read:all',
  }),
  route({
    id: 'messagerie.rattacher',
    methode: 'post',
    chemin: `${V}/fils/{filId}/rattachement`,
    tag: 'messaging',
    resume: 'Rattacher un fil à un dossier et une personne.',
    parametres: ids('filId'),
    corps: E.ThreadAttachSchema,
    reponse: E.ThreadSchema,
    permission: 'message:write:dossier',
    actionAudit: 'fil.rattachement',
  }),
  route({
    id: 'messagerie.envoyer',
    methode: 'post',
    chemin: `${V}/messages-sortants`,
    tag: 'messaging',
    resume: 'Préparer, programmer ou envoyer un message.',
    corps: E.OutboundCreateSchema,
    reponse: E.OutboundMessageSchema,
    permission: 'message:write:dossier',
    actionAudit: 'message.programmation',
    erreurs: [
      'MANDATE_REQUIRED',
      'CONSENT_MISSING',
      'CONTACT_RESTRICTED',
      'WEEKLY_CAP_REACHED',
      'OUTSIDE_SENDING_HOURS',
      'IMPERSONATION_READ_ONLY',
    ],
  }),
  route({
    id: 'messagerie.sortants.lister',
    methode: 'get',
    chemin: `${V}/messages-sortants`,
    tag: 'messaging',
    resume: 'Messages préparés pour le WhatsApp d’Isabelle, en attente de sa validation (RFC-L51-001 §2).',
    requete: z.object({ etat: z.enum(['pending_validation']) }),
    reponse: z.array(E.OutboundMessageSchema),
    permission: 'message.isabelle:validate:all',
  }),
  route({
    id: 'messagerie.valider',
    methode: 'post',
    chemin: `${V}/messages-sortants/{messageId}/validation`,
    tag: 'messaging',
    resume: 'Valider ou rejeter un message préparé pour le WhatsApp d’Isabelle.',
    parametres: ids('messageId'),
    corps: E.OutboundValidationSchema,
    reponse: E.OutboundMessageSchema,
    permission: 'message.isabelle:validate:all',
    actionAudit: 'message.validation',
  }),
  route({
    id: 'messagerie.apercuLien',
    methode: 'get',
    chemin: `${V}/apercu-lien`,
    tag: 'messaging',
    resume: 'Aperçu exact du lien de l’application (générique, sans donnée personnelle).',
    requete: z.object({ page: E.LandingSchema }),
    reponse: E.LinkPreviewSchema,
    permission: 'message:write:dossier',
  }),
];

const followups: RouteContrat[] = [
  route({
    id: 'relances.plans',
    methode: 'get',
    chemin: `${V}/relances`,
    tag: 'followups',
    resume: 'Relances programmées, en pause ou en attente de validation.',
    requete: z.object({ dossierId: IdSchema.optional(), etat: z.enum(['actif', 'pause', 'arrete', 'termine']).optional() }),
    reponse: z.array(E.FollowupPlanSchema),
    permission: 'followup:read:all',
  }),
  route({
    id: 'relances.pause',
    methode: 'post',
    chemin: `${V}/relances/{planId}/pause`,
    tag: 'followups',
    resume: 'Mettre en pause « on laisse respirer ».',
    parametres: ids('planId'),
    corps: E.FollowupPauseSchema,
    reponse: E.FollowupPlanSchema,
    permission: 'followup:write:dossier',
    actionAudit: 'relance.pause',
  }),
  route({
    id: 'relances.reprise',
    methode: 'post',
    chemin: `${V}/relances/{planId}/reprise`,
    tag: 'followups',
    resume: 'Reprendre les relances.',
    parametres: ids('planId'),
    reponse: E.FollowupPlanSchema,
    permission: 'followup:write:dossier',
    actionAudit: 'relance.reprise',
    erreurs: ['CONSENT_MISSING'],
  }),
  route({
    id: 'relances.simuler',
    methode: 'post',
    chemin: `${V}/relances/simulation`,
    tag: 'followups',
    resume: 'Simuler la cadence d’une personne, sans rien programmer.',
    statut: 200,
    corps: E.CadenceSimulationRequestSchema,
    reponse: E.CadenceSimulationSchema,
    permission: 'followup:read:all',
    actionAudit: 'relance.simulation',
  }),
];

const templates: RouteContrat[] = [
  route({
    id: 'modeles.lister',
    methode: 'get',
    chemin: `${V}/modeles`,
    tag: 'templates',
    resume: 'Modèles de messages et leurs variables.',
    reponse: z.array(E.TemplateSchema),
    permission: 'template:read:all',
  }),
  route({
    id: 'modeles.versionner',
    methode: 'post',
    chemin: `${V}/modeles/{code}/versions`,
    tag: 'templates',
    resume: 'Créer une version d’un modèle (contrôles : vouvoiement, signature, mandant).',
    parametres: z.object({ code: z.string().regex(/^[A-Z]\d$/) }),
    corps: E.TemplateVersionCreateSchema,
    reponse: E.TemplateSchema,
    permission: 'template:write:all',
    actionAudit: 'modele.version',
  }),
];

const billing: RouteContrat[] = [
  route({
    id: 'facturation.dossier',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/facturation`,
    tag: 'billing',
    resume: 'Facturation du dossier : règles, lignes par héritier, reste dû.',
    parametres: D,
    reponse: E.BillingStaffViewSchema,
    permission: 'billing:read:dossier',
  }),
  route({
    id: 'facturation.regles',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/facturation/regles`,
    tag: 'billing',
    resume: 'Nouvelle version des règles cochées (confirmation explicite).',
    parametres: D,
    corps: E.BillingRuleSetCreateSchema,
    reponse: E.BillingRuleSetSchema,
    permission: 'billing:write:dossier',
    actionAudit: 'facturation.regles',
    erreurs: ['CONFIRMATION_REQUIRED'],
  }),
  route({
    id: 'facturation.collecte',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/collecte`,
    tag: 'billing',
    resume: 'Calendrier de collecte avant la signature.',
    parametres: D,
    reponse: E.CollectionScheduleSchema,
    permission: 'billing:read:dossier',
  }),
  route({
    id: 'facturation.echeancier',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/collecte/echeanciers`,
    tag: 'billing',
    resume: 'Créer un échéancier pour un héritier.',
    parametres: D,
    corps: E.InstallmentPlanCreateSchema,
    reponse: E.CollectionScheduleSchema,
    permission: 'billing:write:dossier',
    actionAudit: 'facturation.echeancier',
    erreurs: ['CONFIRMATION_REQUIRED'],
  }),
  route({
    id: 'facturation.factures.emettre',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/factures`,
    tag: 'billing',
    resume: 'Émettre une facture (immuable ensuite).',
    parametres: D,
    corps: E.InvoiceIssueSchema,
    reponse: E.InvoiceSchema,
    permission: 'billing:write:dossier',
    actionAudit: 'facture.emission',
    erreurs: ['CONFIRMATION_REQUIRED'],
  }),
  // PARITE-5 : suppléments en un clic (RG-25), sous les permissions de facturation existantes ; repris par la règle « frais réels ».
  route({
    id: 'facturation.supplements.lister',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/facturation/supplements`,
    tag: 'billing',
    resume: 'Suppléments du dossier, catalogue versionné et totaux (équipe de bureau).',
    parametres: D,
    reponse: E.BillingSupplementsSchema,
    permission: 'billing:read:dossier',
  }),
  route({
    id: 'facturation.supplements.ajouter',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/facturation/supplements`,
    tag: 'billing',
    resume: 'Ajouter un supplément du catalogue (montant de la grille, ou saisi tant qu’elle ne le fixe pas).',
    parametres: D,
    corps: E.BillingSupplementCreateSchema,
    reponse: E.BillingSupplementSchema,
    permission: 'billing:write:dossier',
    actionAudit: 'facturation.supplement_ajout',
  }),
  route({
    id: 'facturation.supplements.retirer',
    methode: 'delete',
    chemin: `${V}/supplements/{supplementId}`,
    tag: 'billing',
    resume: 'Retirer un supplément pas encore facturé.',
    parametres: ids('supplementId'),
    statut: 204,
    descriptionReponse: 'Supplément retiré.',
    permission: 'billing:write:dossier',
    actionAudit: 'facturation.supplement_retrait',
  }),
  // Lignes de prestation détaillées (30/10/2026) : catalogue versionné, devis à lignes figées, PDF du devis et de la facture.
  route({
    id: 'facturation.prestations.lister',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/facturation/prestations`,
    tag: 'billing',
    resume: 'Catalogue versionné des prestations (code, intitulé, détail ; aucun tarif).',
    parametres: D,
    reponse: E.PrestationsCatalogueSchema,
    permission: 'billing:read:dossier',
  }),
  route({
    id: 'facturation.devis.lister',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/devis`,
    tag: 'billing',
    resume: 'Devis du dossier et leurs lignes détaillées.',
    parametres: D,
    reponse: z.array(E.QuoteSchema),
    permission: 'billing:read:dossier',
  }),
  route({
    id: 'facturation.devis.creer',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/devis`,
    tag: 'billing',
    resume: 'Créer un devis : chaque ligne garde une copie figée de la prestation (ou le détail saisi d’une ligne libre).',
    parametres: D,
    corps: E.QuoteCreateSchema,
    reponse: E.QuoteSchema,
    permission: 'billing:write:dossier',
    actionAudit: 'facturation.devis_creation',
  }),
  route({
    id: 'facturation.devis.pdf',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/devis/{devisId}/pdf`,
    tag: 'billing',
    resume: 'PDF du devis (désignation complète de chaque ligne), déposé en GED.',
    parametres: ids('dossierId', 'devisId'),
    reponse: E.ExportSchema,
    permission: 'billing:read:dossier',
    actionAudit: 'facturation.devis.export',
  }),
  route({
    id: 'facturation.factures.pdf',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/factures/{factureId}/pdf`,
    tag: 'billing',
    resume: 'PDF de la facture (désignation complète de chaque ligne), déposé en GED.',
    parametres: ids('dossierId', 'factureId'),
    reponse: E.ExportSchema,
    permission: 'billing:read:dossier',
    actionAudit: 'facturation.facture.export',
  }),
  // PARITE-5 : compensations entre héritiers (RG-28), en lecture : calcul « à valider » tant que la règle du foncier n'est pas décidée.
  route({
    id: 'compensations.dossier',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/compensations`,
    tag: 'billing',
    resume: 'Sommes entre héritiers (avances) et pistes de remboursement, dont le foncier ; calcul à valider.',
    parametres: D,
    reponse: E.CompensationsDossierSchema,
    permission: 'compensation:read:dossier',
  }),
];

const payments: RouteContrat[] = [
  route({
    id: 'paiements.liens',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/liens-paiement`,
    tag: 'payments',
    resume: 'Créer des liens de paiement (Stripe, GoCardless).',
    parametres: D,
    corps: E.PaymentLinkRequestSchema,
    reponse: z.array(E.PaymentLinkSchema),
    permission: 'payment:write:dossier',
    actionAudit: 'paiement.liens',
  }),
];

const privacy: RouteContrat[] = [
  route({
    id: 'confidentialite.consentements',
    methode: 'get',
    chemin: `${V}/personnes/{personId}/consentements`,
    tag: 'privacy',
    resume: 'Consentements datés d’une personne.',
    parametres: ids('personId'),
    reponse: z.array(E.ConsentSchema),
    permission: 'consent:read:dossier',
  }),
  route({
    id: 'confidentialite.consentements.enregistrer',
    methode: 'post',
    chemin: `${V}/personnes/{personId}/consentements`,
    tag: 'privacy',
    resume: 'Enregistrer un consentement recueilli.',
    parametres: ids('personId'),
    corps: E.ConsentRecordSchema,
    reponse: E.ConsentSchema,
    permission: 'consent:write:dossier',
    actionAudit: 'consentement.enregistrement',
  }),
  route({
    id: 'confidentialite.sensibles',
    methode: 'get',
    chemin: `${V}/personnes/{personId}/sensibles`,
    tag: 'privacy',
    resume: 'Étiquettes sensibles (équipe MAAT seulement).',
    parametres: ids('personId'),
    reponse: z.array(E.SensitiveLabelSchema),
    permission: 'sensitive:read:dossier',
  }),
  // RFC-L55-001 point 1 (ARB-VERROU-05) : qui peut recevoir un partage hors équipe, pour qui a le droit de partager.
  route({
    id: 'confidentialite.destinataires',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/destinataires-partage`,
    tag: 'privacy',
    resume: 'Externes rattachés au dossier à qui un partage hors équipe peut être proposé (jamais d’office).',
    parametres: D,
    reponse: z.array(E.DestinatairePartageSchema),
    permission: 'sensitive:share:dossier',
  }),
  route({
    id: 'confidentialite.sensibles.partager',
    methode: 'post',
    chemin: `${V}/sensibles/{etiquetteId}/partages`,
    tag: 'privacy',
    resume: 'Partager une étiquette hors équipe, après l’alerte qui liste les destinataires.',
    parametres: ids('etiquetteId'),
    corps: E.SensitiveShareSchema,
    reponse: E.SensitiveLabelSchema,
    permission: 'sensitive:share:dossier',
    actionAudit: 'sensible.partage',
    erreurs: ['CONFIRMATION_REQUIRED'],
  }),
  route({
    id: 'confidentialite.notes',
    methode: 'get',
    chemin: `${V}/dossiers/{dossierId}/notes`,
    tag: 'privacy',
    resume: 'Notes internes (coulisses).',
    parametres: D,
    reponse: z.array(E.InternalNoteSchema),
    permission: 'note:read:dossier',
  }),
  route({
    id: 'confidentialite.notes.ajouter',
    methode: 'post',
    chemin: `${V}/dossiers/{dossierId}/notes`,
    tag: 'privacy',
    resume: 'Ajouter une note interne.',
    parametres: D,
    corps: E.InternalNoteCreateSchema,
    reponse: E.InternalNoteSchema,
    permission: 'note:write:dossier',
    actionAudit: 'note.ajout',
  }),
  route({
    id: 'confidentialite.demandes',
    methode: 'post',
    chemin: `${V}/demandes-rgpd`,
    tag: 'privacy',
    resume: 'Déposer une demande RGPD (accès, rectification, suppression, opposition).',
    corps: E.PrivacyRequestCreateSchema,
    reponse: E.PrivacyRequestSchema,
    permission: 'privacy:write:self',
    actionAudit: 'rgpd.demande',
  }),
];

const admin: RouteContrat[] = [
  route({
    id: 'admin.utilisateurs',
    methode: 'get',
    chemin: `${V}/admin/utilisateurs`,
    tag: 'admin',
    resume: 'Utilisateurs et rôles.',
    requete: PageQuerySchema,
    reponse: pageDe(E.UserSchema),
    permission: 'admin:read:all',
  }),
  route({
    id: 'admin.utilisateurs.creer',
    methode: 'post',
    chemin: `${V}/admin/utilisateurs`,
    tag: 'admin',
    resume: 'Créer un utilisateur et ses rôles.',
    corps: E.UserCreateSchema,
    reponse: E.UserSchema,
    permission: 'admin:write:all',
    actionAudit: 'utilisateur.creation',
  }),
  route({
    id: 'admin.etudes',
    methode: 'get',
    chemin: `${V}/admin/etudes`,
    tag: 'admin',
    resume: 'Études notariales et clercs.',
    reponse: z.array(E.EtudeSchema),
    permission: 'admin:read:all',
  }),
  route({
    id: 'admin.etudes.creer',
    methode: 'post',
    chemin: `${V}/admin/etudes`,
    tag: 'admin',
    resume: 'Ajouter une étude.',
    corps: E.EtudeWriteSchema,
    reponse: E.EtudeSchema,
    permission: 'admin:write:all',
    actionAudit: 'etude.creation',
  }),
  route({
    id: 'admin.etudes.modifier',
    methode: 'patch',
    chemin: `${V}/admin/etudes/{etudeId}`,
    tag: 'admin',
    resume: 'Modifier une étude.',
    parametres: ids('etudeId'),
    corps: E.EtudeWriteSchema.partial(),
    reponse: E.EtudeSchema,
    permission: 'admin:write:all',
    actionAudit: 'etude.modification',
  }),
  route({
    id: 'admin.etudes.supprimer',
    methode: 'delete',
    chemin: `${V}/admin/etudes/{etudeId}`,
    tag: 'admin',
    resume: 'Supprimer une étude (suppression logique).',
    parametres: ids('etudeId'),
    statut: 204,
    descriptionReponse: 'Étude supprimée.',
    permission: 'admin:write:all',
    actionAudit: 'etude.suppression',
  }),
  route({
    id: 'admin.parametresLegaux',
    methode: 'get',
    chemin: `${V}/admin/parametres-legaux`,
    tag: 'admin',
    resume: 'Paramètres légaux datés.',
    reponse: z.array(LegalParameterSchema),
    permission: 'admin:read:all',
  }),
  route({
    id: 'admin.parametresLegaux.versionner',
    methode: 'post',
    chemin: `${V}/admin/parametres-legaux`,
    tag: 'admin',
    resume: 'Ajouter une valeur datée d’un paramètre légal.',
    corps: LegalParameterCreateSchema,
    reponse: LegalParameterSchema,
    permission: 'admin:write:all',
    actionAudit: 'parametre_legal.version',
  }),
];

const beta: RouteContrat[] = [
  route({
    id: 'beta.questionsDues',
    methode: 'get',
    chemin: `${V}/beta/questions`,
    tag: 'beta',
    resume: 'Questions BETA dues pour un écran ou un événement.',
    requete: z.object({ contexte: z.string().min(2) }),
    reponse: z.array(E.BetaQuestionViewSchema),
    permission: 'beta:read:self',
  }),
  route({
    id: 'beta.repondre',
    methode: 'post',
    chemin: `${V}/beta/questions/{cle}/reponses`,
    tag: 'beta',
    resume: 'Répondre à une question BETA.',
    parametres: z.object({ cle: z.string().regex(/^[a-z_]+(\.[a-z_0-9]+)*$/) }),
    corps: E.BetaAnswerSchema,
    reponse: E.LearnedSettingSchema,
    permission: 'beta:write:self',
    actionAudit: 'beta.reponse',
  }),
  route({
    id: 'beta.reglages',
    methode: 'get',
    chemin: `${V}/beta/reglages`,
    tag: 'beta',
    resume: 'Réglages appris.',
    reponse: z.array(E.LearnedSettingSchema),
    permission: 'beta:read:self',
  }),
];

const portal: RouteContrat[] = [
  route({
    id: 'portail.accueil',
    methode: 'get',
    chemin: `${V}/portail`,
    tag: 'portal',
    resume: 'Empreinte de progression et action unique.',
    reponse: E.PortalHomeSchema,
    permission: 'portal:read:branche',
  }),
  route({
    id: 'portail.branche',
    methode: 'get',
    chemin: `${V}/portail/branche`,
    tag: 'portal',
    resume: 'Ma place dans la famille (ma branche seulement).',
    reponse: z.array(E.PersonFamilyViewSchema),
    permission: 'portal:read:branche',
  }),
  route({
    id: 'portail.paiement',
    methode: 'get',
    chemin: `${V}/portail/paiement`,
    tag: 'portal',
    resume: 'Mon reste dû, ou « Vous êtes à jour ».',
    reponse: E.BillingClientViewSchema,
    permission: 'portal:read:branche',
  }),
  route({
    id: 'portail.journal',
    methode: 'get',
    chemin: `${V}/portail/journal`,
    tag: 'portal',
    resume: 'Mes échanges avec le cabinet.',
    reponse: z.array(E.PortalTimelineEntrySchema),
    permission: 'portal:read:branche',
  }),
  route({
    id: 'portail.prestations',
    methode: 'get',
    chemin: `${V}/portail/prestations`,
    tag: 'portal',
    resume: 'Ce que couvrent mes devis et mes factures : désignations détaillées, aucun montant.',
    reponse: E.PortalPrestationsSchema,
    permission: 'portal:read:branche',
  }),
  route({
    id: 'portail.videos',
    methode: 'get',
    chemin: `${V}/portail/videos`,
    tag: 'portal',
    resume: 'Vidéos que le cabinet a partagées avec la famille : titre et lecteur seulement.',
    reponse: z.array(E.PortalVideoSchema),
    permission: 'portal:read:branche',
  }),
  route({
    id: 'portail.confidentialite',
    methode: 'get',
    chemin: `${V}/portail/confidentialite`,
    tag: 'portal',
    resume: 'Mes données et ma confidentialité.',
    reponse: E.PortalPrivacySchema,
    permission: 'portal:read:branche',
  }),
  route({
    id: 'portail.confidentialite.modifier',
    methode: 'post',
    chemin: `${V}/portail/confidentialite`,
    tag: 'portal',
    resume: 'Modifier mes consentements et mon canal préféré.',
    corps: E.PortalPrivacyUpdateSchema,
    reponse: E.PortalPrivacySchema,
    permission: 'portal:write:self',
    actionAudit: 'portail.confidentialite',
    erreurs: ['OTP_REQUIRED'],
  }),
  route({
    id: 'portail.pieces.deposer',
    methode: 'post',
    chemin: `${V}/portail/pieces`,
    tag: 'portal',
    resume: 'Envoyer une pièce par photo (téléversement reprenable).',
    corps: E.PortalPieceUploadSchema,
    reponse: E.PortalUploadSessionSchema,
    permission: 'portal:write:self',
    actionAudit: 'portail.piece',
  }),
  route({
    id: 'portail.pieces.demander',
    methode: 'post',
    chemin: `${V}/portail/pieces/demande`,
    tag: 'portal',
    resume: '« Je ne l’ai pas : demandez-le pour moi » : le cabinet demande la pièce attendue.',
    corps: E.PortalPieceDelegationSchema,
    statut: 200,
    reponse: E.PortalPieceDelegationResultSchema,
    permission: 'portal:write:self',
    actionAudit: 'piece.demande_deleguee',
  }),
];

const clerc: RouteContrat[] = [
  route({
    id: 'clerc.dossiers',
    methode: 'get',
    chemin: `${V}/clerc/dossiers`,
    tag: 'clerc',
    resume: 'Dossiers transmis à mon étude, checklist et pièces manquantes.',
    reponse: z.array(E.ClercDossierSchema),
    permission: 'clerc:read:etude',
  }),
];

const notaire: RouteContrat[] = [
  route({
    id: 'notaire.dossier',
    methode: 'get',
    chemin: `${V}/notaire/dossiers/{dossierId}`,
    tag: 'notaire',
    resume: 'Tableaux des vivants et des défunts, titres, actes.',
    parametres: D,
    reponse: E.NotaireDossierSchema,
    permission: 'notaire:read:dossier',
  }),
];

const sync: RouteContrat[] = [
  route({
    id: 'sync.push',
    methode: 'post',
    chemin: `${V}/sync/push`,
    tag: 'sync',
    resume: 'Envoyer les mutations faites hors ligne.',
    corps: E.SyncPushSchema,
    reponse: E.SyncPushResultSchema,
    permission: 'sync:write:self',
    actionAudit: 'sync.push',
  }),
  route({
    id: 'sync.pull',
    methode: 'get',
    chemin: `${V}/sync/pull`,
    tag: 'sync',
    resume: 'Récupérer les changements depuis un curseur.',
    requete: z.object({ depuis: z.string().regex(/^\d+$/) }),
    reponse: E.SyncPullSchema,
    permission: 'sync:write:self',
  }),
];

const webhook = (id: string, fournisseur: string, resume: string): RouteContrat =>
  route({
    id,
    methode: 'post',
    chemin: `${V}/webhooks/${fournisseur}`,
    tag: 'webhooks',
    resume,
    statut: 202,
    corps: E.WebhookPayloadSchema,
    reponse: E.AcceptedSchema,
    descriptionReponse: 'Reçu, traité de façon idempotente.',
    permission: 'public',
    actionAudit: `webhook.${fournisseur}`,
    erreurs: ['SIGNATURE_INVALID'],
  });

const webhooks: RouteContrat[] = [
  webhook('webhooks.jockalia', 'jockalia', 'Événements WhatsApp (Robot Manager) : messages entrants, statuts.'),
  webhook('webhooks.email', 'email', 'E-mails entrants et rebonds.'),
  webhook('webhooks.stripe', 'stripe', 'Événements de paiement Stripe.'),
  webhook('webhooks.gocardless', 'gocardless', 'Événements de prélèvement GoCardless.'),
  webhook('webhooks.docuseal', 'docuseal', 'Événements de signature DocuSeal.'),
];

const system: RouteContrat[] = [
  route({
    id: 'systeme.sante',
    methode: 'get',
    chemin: '/health',
    tag: 'system',
    resume: 'État de santé du service.',
    reponse: E.HealthSchema,
    permission: 'public',
  }),
  route({
    id: 'systeme.version',
    methode: 'get',
    chemin: '/version',
    tag: 'system',
    resume: 'Identité du build : commit, branche, date, environnement.',
    reponse: E.VersionSchema,
    permission: 'public',
  }),
];

export const ROUTES_PAR_TAG = {
  auth,
  impersonation,
  dossiers,
  procedure,
  tasks,
  decisions,
  genealogy,
  assets,
  estimates,
  notary,
  deadlines,
  documents,
  mandates,
  messaging,
  followups,
  templates,
  billing,
  payments,
  privacy,
  admin,
  beta,
  portal,
  clerc,
  notaire,
  sync,
  webhooks,
  system,
} as const;
